import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';

const EmailVerification = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm();

  // Get email from location state or query params
  const email = location.state?.email || new URLSearchParams(location.search).get('email') || '';

  useEffect(() => {
    if (!email) {
      toast.error('Email address is required for verification');
      navigate('/register');
    } else {
      setValue('email', email);
    }
  }, [email, navigate, setValue]);

  // Countdown timer for resend
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    toast.dismiss();

    try {
      const response = await authAPI.verifyEmailCode(data.email, data.code);
      
      toast.success('Email verified successfully! You can now log in.', {
        duration: 5000
      });
      
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          'Verification failed. Please check your code and try again.';
      toast.error(errorMessage, {
        duration: 5000
      });
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0 || !email) return;

    setIsResending(true);
    toast.dismiss();

    try {
      await authAPI.resendVerificationCode(email);
      toast.success('Verification code has been resent to your email.', {
        duration: 5000
      });
      setResendCooldown(60); // 60 second cooldown
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          'Failed to resend verification code. Please try again.';
      toast.error(errorMessage, {
        duration: 5000
      });
    } finally {
      setIsResending(false);
    }
  };

  const code = watch('code');

  return (
    <div className="auth-page">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <img
            src={`${process.env.PUBLIC_URL}/liaizn-logo.png`}
            alt="Liaizn"
            className="mx-auto h-14 w-auto mb-3 object-contain"
          />
          <h2 className="text-2xl font-bold text-secondary-900">Verify Your Email</h2>
          <p className="mt-2 text-sm text-secondary-600">
            We've sent a 6-digit verification code to
          </p>
          <p className="mt-1 text-sm font-medium text-secondary-900">{email}</p>
          <p className="mt-2 text-sm text-secondary-600">
            Please enter the code below to verify your email address.
          </p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="auth-card">
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <input type="hidden" {...register('email')} />

            <div>
              <label htmlFor="code" className="form-label">
                Verification Code
              </label>
              <input
                id="code"
                type="text"
                inputMode="numeric"
                maxLength="6"
                autoComplete="one-time-code"
                className={`input-field text-center text-2xl font-bold tracking-widest ${errors.code ? 'border-red-500' : ''}`}
                placeholder="000000"
                {...register('code', {
                  required: 'Verification code is required',
                  pattern: {
                    value: /^\d{6}$/,
                    message: 'Code must be 6 digits'
                  }
                })}
                onChange={(e) => {
                  // Only allow digits
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setValue('code', value, { shouldValidate: true });
                }}
              />
              {errors.code && (
                <p className="mt-1 text-sm text-red-600">{errors.code.message}</p>
              )}
              <p className="mt-2 text-xs text-gray-500">
                Enter the 6-digit code sent to your email
              </p>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading || !code || code.length !== 6}
                className="w-full btn-primary py-3 text-base disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Verifying...' : 'Verify Email'}
              </button>
            </div>

            <div className="text-center">
              <p className="text-sm text-gray-600">
                Didn't receive the code?
              </p>
              <button
                type="button"
                onClick={handleResendCode}
                disabled={isResending || resendCooldown > 0}
                className="mt-2 text-sm font-medium text-primary-600 hover:text-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resendCooldown > 0 
                  ? `Resend code in ${resendCooldown}s` 
                  : isResending 
                    ? 'Sending...' 
                    : 'Resend Verification Code'}
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/register')}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                Back to Registration
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="mt-8 text-center">
        <p className="text-sm text-gray-500">
          The verification code will expire in 10 minutes
        </p>
      </div>
    </div>
  );
};

export default EmailVerification;




